import {
  faCalendarAlt,
  faClipboardList,
  faClock,
  faComment,
  faMapMarkerAlt,
  faMoneyCheckDollar,
  faUserGroup,
  faUsers,
  faIdCard,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useMemo } from 'react';
import cn from 'classnames';
import { useSearchParamsUpdater } from 'hooks/useSearchParamsUpdater';
import { getEntityLink } from 'helpers/getEntityLink';
import { formatDate } from 'helpers/formatDate';
import { SERVICE_TYPES_UA } from 'constants/serviceTypes';
import { entryStatuses } from 'constants/entryStatuses';
import { BackButton } from 'components/BackButton';
import { NoResults } from 'components/NoResults';
import { ChooseLocation } from 'common/ChooseLocation';
import { ProfileHeader } from 'common/ProfileHeader';
import { PaymentsCell } from 'pages/Schedule/PaymentsCell';
import { PersonCell } from 'pages/Schedule/PersonCell';
import { BalanceTab } from './BalanceTab';
import { Payments } from './PaymentsTab';
import { SubscriptionsTab } from './SubscriptionsTab';
import { Actions } from './Actions';
import styles from 'hocs/withEntityPageTemplate/styles.scss';

export const EntityPageTemplate = ({
  backButtonText,
  backButtonPath,
  entity,
  entityType,
  entries,
  renderProfileExtra,
  onEntityNameUpdate,
  noResultsText,
  children,
}) => {
  const { locations } = useSelector((state) => state.locations);
  const query = useSearchParamsUpdater();
  const { locationName } = useParams();

  const locationTransliteration = query.getParam('location') || locationName;

  const location = useMemo(
    () => locations.find((l) => l.transliteration === locationTransliteration),
    [locations, locationTransliteration]
  );

  const locationEntries = useMemo(() => entries.filter((e) => e.locationName === location?.name), [entries, location]);

  const formatTime = (time) => time.slice(0, 5);

  const getStatus = (status) => entryStatuses.find(({ key }) => key === status);

  const isGroupBooking = (booking) => booking.group !== null;

  const selectedTab = query.getParam('tab');

  return (
    <section className={styles.page}>
      <div className={styles.header}>
        <div>
          <BackButton text={backButtonText} path={getEntityLink(backButtonPath)} />
          <ChooseLocation className={styles.locationSelector} />
          <ProfileHeader entity={entity} renderExtra={renderProfileExtra} onEntityNameUpdate={onEntityNameUpdate} />
          <div className={styles.tabs}>
            <button
              className={cn(styles.tab, { [styles.active]: !selectedTab || selectedTab === 'attendance' })}
              onClick={() => query.setParam('tab', 'attendance')}
              disabled={!selectedTab || selectedTab === 'attendance'}
            >
              <FontAwesomeIcon icon={faCalendarAlt} />
              Графік відвідування
            </button>
            <button
              className={cn(styles.tab, { [styles.active]: selectedTab === 'payments' })}
              onClick={() => query.setParam('tab', 'payments')}
              disabled={selectedTab === 'payments'}
            >
              <FontAwesomeIcon icon={faMoneyCheckDollar} />
              Рахунки
            </button>
            {entityType === 'client' && (
              <>
                <button
                  className={cn(styles.tab, { [styles.active]: selectedTab === 'subscriptions' })}
                  onClick={() => query.setParam('tab', 'subscriptions')}
                  disabled={selectedTab === 'subscriptions'}
                >
                  <FontAwesomeIcon icon={faIdCard} />
                  Пакети
                </button>
                <button
                  className={cn(styles.tab, { [styles.active]: selectedTab === 'audit' })}
                  onClick={() => query.setParam('tab', 'audit')}
                  disabled={selectedTab === 'audit'}
                >
                  <FontAwesomeIcon icon={faClipboardList} />
                  Керування балансом
                </button>
              </>
            )}
          </div>
        </div>
        {children}
      </div>
      {(!selectedTab || selectedTab === 'attendance') && entries.length > 0 && (
        <div className={styles.table}>
          <div className={cn(styles.tableRow, styles.tableRowHeader)}>
            <div className={styles.tableCell}>Послуга</div>
            <div className={styles.tableCell}>Дата та час</div>
            <div className={styles.tableCell}>Зала</div>
            <div className={styles.tableCell}>Учасники</div>
            <div className={styles.tableCell}>Тренери</div>
            <div className={styles.tableCell}>Оплата</div>
            <div className={cn(styles.tableCell, styles.tableCellStatus)}>Статус</div>
          </div>

          {locationEntries.map((booking) => (
            <div className={styles.tableRow} key={booking.id}>
              <div className={cn(styles.tableCell, styles.service)}>
                <FontAwesomeIcon icon={isGroupBooking(booking) ? faUsers : faUserGroup} className={styles.icon} />
                <div className={styles.info}>
                  <span className={styles.name}>{SERVICE_TYPES_UA[booking.service]}</span>
                  {booking.comment && (
                    <div className={styles.comment}>
                      <FontAwesomeIcon icon={faComment} />"{booking.comment}"
                    </div>
                  )}
                </div>
              </div>

              <div className={cn(styles.tableCell, styles.time)}>
                <FontAwesomeIcon icon={faClock} />
                <div>
                  <span className={styles.date}>{formatDate(booking.date, 'UA', true)}</span>
                  <span>
                    {formatTime(booking.startTime)} {booking.endTime && `- ${formatTime(booking.endTime)}`}
                  </span>
                </div>
              </div>

              <div className={cn(styles.tableCell, styles.location)}>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <span className={styles.hallName}>{booking.hallName}</span>
              </div>

              <PersonCell
                booking={booking}
                people={booking.participants}
                isGroup={isGroupBooking(booking)}
                role='client'
              />

              <PersonCell booking={booking} people={booking.coaches} isGroup={isGroupBooking(booking)} role='coach' />

              <PaymentsCell booking={booking} />

              <div className={cn(styles.tableCell, styles.tableCellStatus)}>
                <div className={cn(styles.status, styles[getStatus(booking.status).key])}>
                  <FontAwesomeIcon icon={getStatus(booking.status).icon} />
                  {getStatus(booking.status).name}
                </div>
              </div>

              <Actions className={styles.actions} entry={booking} />
            </div>
          ))}
        </div>
      )}
      {(!selectedTab || selectedTab === 'attendance') && entries.length === 0 && <NoResults text={noResultsText} />}
      {selectedTab === 'subscriptions' && entityType === 'client' && (
        <SubscriptionsTab entity={entity} entityType={entityType} />
      )}
      {selectedTab === 'payments' && <Payments entity={entity} entityType={entityType} />}
      {selectedTab === 'audit' && <BalanceTab />}
    </section>
  );
};
